import { createCRUDHook } from './createCRUDHook';
import {
  PartWorkflowDto,
  JsonPatch,
  PartWorkflowPositionsDto,
  FilterOptionModel,
  ApprovalPartDetailDto,
  MarkAsApproveOrRejectWorkflowProcessDto,
  WorkflowPositionProcessDto,
  NextWorkflowPositionDto,
  WorkflowPositionHistoryDto,
} from 'utils/models';
import { AxiosResponse } from 'axios';
import {
  FILTERED_ENTITIES,
  GET_APPROVAL_PART_DETAIL_BY_STAFF_ID,
  GET_NEXT_WORKFLOW_POSITION_DATA,
  GET_POSITION_HISTORY_BY_PART_ID,
  MARK_AS_APPROVE_OR_REJECT_WORKFLOW_PROCESS,
  START_WORKFLOW_APPROVAL_PROCESS,
  TBL_WORKFLOW,
  TBL_WORKFLOW_POSITION,
  TBL_WORKFLOW_POSITION_PROCESS,
} from './apiEndPoints';
import { prepareQueryParams } from 'utils';

interface WorkflowCustomOperations {
  getWorkflowPositionsData: () => Promise<PartWorkflowPositionsDto[]>;
  addWorkflowPositionsData: (
    param: PartWorkflowPositionsDto
  ) => Promise<PartWorkflowPositionsDto>;
  updateWorkflowPositionsData: (
    param: JsonPatch[],
    id: number
  ) => Promise<AxiosResponse>;
  deleteWorkflowPositionsData: (id: number) => Promise<boolean>;
  getWorkflowPositionsDataById: (
    id: number
  ) => Promise<PartWorkflowPositionsDto>;
  getWorkflowPositionsDataByFE: (
    filteredEntities: FilterOptionModel
  ) => Promise<PartWorkflowPositionsDto[]>;

  startWorkflowApprovalProcess: (
    partId: number,
    workflowId: number
  ) => Promise<PartWorkflowPositionsDto>;

  getApprovalPartDetailByStaffId: (
    staffId: number
  ) => Promise<ApprovalPartDetailDto[]>;

  markAsApproveOrRejectWorkflowProcess: (
    param: MarkAsApproveOrRejectWorkflowProcessDto
  ) => Promise<boolean>;

  updateWorkFlowPositionProcess: (
    param: JsonPatch[],
    id: number
  ) => Promise<AxiosResponse>;

  getNextWorkflowPositionData: (
    partId: number,
    workflowId: number,
    fromStart: boolean
  ) => Promise<NextWorkflowPositionDto>;

  getWorkflowPositionsProcessDataByFE: (
    filteredEntities: FilterOptionModel
  ) => Promise<WorkflowPositionProcessDto[]>;

  addWorkflowPositionProccessData: (
    param: WorkflowPositionProcessDto
  ) => Promise<WorkflowPositionProcessDto>;

  getPositionHistoryByPartId: (
    partId: number
  ) => Promise<WorkflowPositionHistoryDto[]>;
}

const { Context, useManager, useConsumer } = createCRUDHook<
  PartWorkflowDto,
  PartWorkflowDto[],
  WorkflowCustomOperations
>({
  entityName: 'Workflow',
  endpoint: TBL_WORKFLOW,

  customOperations: (auth) => ({
    getWorkflowPositionsData: async (): Promise<PartWorkflowPositionsDto[]> => {
      try {
        let res = await auth.makeAuthenticatedGet(TBL_WORKFLOW_POSITION);
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    addWorkflowPositionsData: async (
      param: PartWorkflowPositionsDto
    ): Promise<PartWorkflowPositionsDto> => {
      try {
        let res = await auth.makeAuthenticatedPost(
          TBL_WORKFLOW_POSITION,
          param
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    updateWorkflowPositionsData: async (
      param: JsonPatch[],
      id: number
    ): Promise<AxiosResponse> => {
      try {
        let res = await auth.makeAuthenticatedPatch(
          `${TBL_WORKFLOW_POSITION}/${id}`,
          param
        );
        return Promise.resolve(res);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    deleteWorkflowPositionsData: async (id: number): Promise<boolean> => {
      try {
        const res = await auth.makeAuthenticatedDelete(
          `${TBL_WORKFLOW_POSITION}/${id}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return false;
      }
    },
    getWorkflowPositionsDataById: async (
      id: number
    ): Promise<PartWorkflowPositionsDto> => {
      try {
        let res = await auth.makeAuthenticatedGet(
          `${TBL_WORKFLOW_POSITION}/${id}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    getWorkflowPositionsDataByFE: async (
      filteredEntities: FilterOptionModel
    ): Promise<PartWorkflowPositionsDto[]> => {
      try {
        let res = await auth.makeAuthenticatedGet(
          TBL_WORKFLOW_POSITION +
            FILTERED_ENTITIES +
            `?${prepareQueryParams(filteredEntities)}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    startWorkflowApprovalProcess: async (
      partId: number,
      workflowId: number
    ): Promise<PartWorkflowPositionsDto> => {
      try {
        let res = await auth.makeAuthenticatedGet(
          TBL_WORKFLOW_POSITION_PROCESS +
            START_WORKFLOW_APPROVAL_PROCESS +
            `?partId=${partId}&workflowId=${workflowId}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    getApprovalPartDetailByStaffId: async (
      staffId: number
    ): Promise<ApprovalPartDetailDto[]> => {
      try {
        let res = await auth.makeAuthenticatedGet(
          TBL_WORKFLOW_POSITION_PROCESS +
            GET_APPROVAL_PART_DETAIL_BY_STAFF_ID +
            `?staffId=${staffId}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    markAsApproveOrRejectWorkflowProcess: async (
      param: MarkAsApproveOrRejectWorkflowProcessDto
    ): Promise<boolean> => {
      try {
        let res = await auth.makeAuthenticatedPost(
          TBL_WORKFLOW_POSITION_PROCESS +
            MARK_AS_APPROVE_OR_REJECT_WORKFLOW_PROCESS,
          param
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    updateWorkFlowPositionProcess: async (
      param: JsonPatch[],
      id: number
    ): Promise<AxiosResponse> => {
      try {
        let res = await auth.makeAuthenticatedPatch(
          TBL_WORKFLOW_POSITION_PROCESS + `/${id}`,
          param
        );
        return Promise.resolve(res);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    getNextWorkflowPositionData: async (
      partId: number,
      workflowId: number,
      fromStart: boolean
    ): Promise<NextWorkflowPositionDto> => {
      try {
        let res = await auth.makeAuthenticatedGet(
          TBL_WORKFLOW_POSITION_PROCESS +
            GET_NEXT_WORKFLOW_POSITION_DATA +
            `?partId=${partId}&workflowId=${workflowId}&fromStart=${fromStart}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    getWorkflowPositionsProcessDataByFE: async (
      filteredEntities: FilterOptionModel
    ): Promise<WorkflowPositionProcessDto[]> => {
      try {
        let res = await auth.makeAuthenticatedGet(
          TBL_WORKFLOW_POSITION_PROCESS +
            FILTERED_ENTITIES +
            `?${prepareQueryParams(filteredEntities)}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    addWorkflowPositionProccessData: async (
      param: WorkflowPositionProcessDto
    ): Promise<WorkflowPositionProcessDto> => {
      try {
        let res = await auth.makeAuthenticatedPost(
          TBL_WORKFLOW_POSITION_PROCESS,
          param
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    getPositionHistoryByPartId: async (
      partId: number
    ): Promise<WorkflowPositionHistoryDto[]> => {
      try {
        let res = await auth.makeAuthenticatedGet(
          TBL_WORKFLOW_POSITION_PROCESS +
            GET_POSITION_HISTORY_BY_PART_ID +
            `?draftPartId=${partId}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
  }),
});

export const WorkflowContext = Context;
export const useWorkflowManager = useManager;
export const useWorkflow = useConsumer;
